/**
 * The anti-cheat's heuristics as pure functions (the service, ./anticheat.ts, keeps the state and the marks). Every
 * threshold is in config.anticheat (../config.ts AnticheatConfig); docs/security.md explains each rule, its threshold
 * and why it does not fire for honest players. Nothing here blocks anyone: a rule only describes a MARK for the
 * operators.
 *
 * Income categories (incomeCategory): what a stored change of gold / diamonds is attributed to. The envelope of a
 * category (outsideEnvelope) is what its source can give: a purchase, an operator, a battle may add gold and diamonds;
 * a sale and the tribute only gold; an exchange exactly GOLD_PER_DIAMOND gold per diamond spent; the daily present at
 * most its largest gift; a new profile its starting capital; a shop, the auction and every other op nothing at all.
 */
import { GOLD_PER_DIAMOND } from '../../../game/rules/ops/bank';
import { PRESENT_GOLD } from '../../../game/rules/ops/progress-claims';
import type { CurrencyPair } from '../config';
import type { EconomySource } from './economy';

/** A text in both console languages (the console's LocalizedText). */
export interface Text {
  en: string;
  uk: string;
}

export const text = (en: string, uk: string): Text => ({ en, uk });

export type MarkType = 'resources' | 'battles';
export type MarkSeverity = 'low' | 'medium' | 'high';
export type MarkStatus = 'open' | 'reviewed_ok' | 'confirmed';

export const MARK_TYPES: readonly MarkType[] = ['resources', 'battles'];
export const MARK_SEVERITIES: readonly MarkSeverity[] = ['low', 'medium', 'high'];
export const MARK_STATUSES: readonly MarkStatus[] = ['open', 'reviewed_ok', 'confirmed'];

export const SEVERITY_RANK: Readonly<Record<MarkSeverity, number>> = { low: 1, medium: 2, high: 3 };

export function maxSeverity(a: MarkSeverity, b: MarkSeverity): MarkSeverity {
  return SEVERITY_RANK[a] >= SEVERITY_RANK[b] ? a : b;
}

/** The rules, their mark type and title. */
export const MARK_RULES = {
  // ------------------------------------------------------------------ suspicious resource activity
  unattributed: { type: 'resources', title: text('Gold / diamonds from no known source', 'Золото / алмази з невідомого джерела') },
  outside_source: { type: 'resources', title: text('More than its source can give', 'Більше, ніж може дати джерело') },
  income_rate: { type: 'resources', title: text('Income rate beyond the economy model', 'Темп доходу понад модель економіки') },
  spree: { type: 'resources', title: text('Spending spree with unexplained resources', 'Витрати ресурсів невідомого походження') },
  unverified_reward: { type: 'resources', title: text('Reward of a battle that failed verification', 'Нагорода за бій, що не пройшов перевірку') },
  command_replays: { type: 'resources', title: text('Repeated identical commands', 'Повтори однакових команд') },
  import_balance: { type: 'resources', title: text('Large imported balance', 'Великий імпортований баланс') },
  // ------------------------------------------------------------------ suspicious battle activity
  sim_mismatch: { type: 'battles', title: text("Result the server's simulation does not reproduce", 'Результат, якого не відтворює симуляція сервера') },
  impossible_input: { type: 'battles', title: text('Input the game could not have produced', 'Введення, якого гра не могла створити') },
  endless_battle: { type: 'battles', title: text('Battle that never ends', 'Бій, що не закінчується') },
  fast_finish: { type: 'battles', title: text('Battle finished faster than possible', 'Бій завершено швидше, ніж можливо') },
  online_desync: { type: 'battles', title: text('Online desync caused by this player', 'Розсинхронізація онлайн-бою з боку гравця') },
  online_result: { type: 'battles', title: text('Online result contradicting the simulation', 'Онлайн-результат усупереч симуляції') },
  online_input: { type: 'battles', title: text('Online input the server refused', 'Онлайн-введення, яке сервер відхилив') },
  win_streak: { type: 'battles', title: text('Improbable win streak', 'Неймовірна серія перемог') },
  kills_per_turn: { type: 'battles', title: text('Kills per turn beyond the model', 'Вбивства за хід понад модель') },
} as const satisfies Record<string, { type: MarkType; title: Text }>;

export type MarkRule = keyof typeof MARK_RULES;
export const MARK_RULE_IDS = Object.keys(MARK_RULES) as MarkRule[];

export function isMarkRule(v: unknown): v is MarkRule {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(MARK_RULES, v);
}

// ------------------------------------------------------------------------------------------------ income attribution

export type IncomeCategory =
  | 'start'
  | 'battle'
  | 'achievement'
  | 'task'
  | 'present'
  | 'tribute'
  | 'ad'
  | 'purchase'
  | 'refund'
  | 'exchange'
  | 'sale'
  | 'shop'
  | 'auction'
  | 'admin'
  | 'client_save'
  | 'import'
  | 'session'
  | 'other'
  | 'unknown';

export const INCOME_CATEGORIES: readonly IncomeCategory[] = [
  'start',
  'battle',
  'achievement',
  'task',
  'present',
  'tribute',
  'ad',
  'purchase',
  'refund',
  'exchange',
  'sale',
  'shop',
  'auction',
  'admin',
  'client_save',
  'import',
  'session',
  'other',
  'unknown',
];

export const CATEGORY_TITLES: Readonly<Record<IncomeCategory | 'unattributed', Text>> = {
  start: text('Starting capital', 'Стартовий капітал'),
  battle: text('Battles and graves', 'Бої й могили'),
  achievement: text('Achievements', 'Досягнення'),
  task: text('Tasks', 'Завдання'),
  present: text('Daily present', 'Щоденний подарунок'),
  tribute: text('Zone tribute', 'Данина територій'),
  ad: text('Rewarded ads', 'Реклама з винагородою'),
  purchase: text('Store purchases', 'Покупки в магазині застосунків'),
  refund: text('Refunds taken back', 'Повернення покупок'),
  exchange: text('Diamond exchange', 'Обмін алмазів'),
  sale: text('Sales', 'Продаж'),
  shop: text('Shop and upgrades', 'Магазин і покращення'),
  auction: text('Auction', 'Аукціон'),
  admin: text('Operators (ledger)', 'Оператори (реєстр)'),
  client_save: text('Client saves (L0 / L1)', 'Збереження клієнта (L0 / L1)'),
  import: text('Imported save', 'Імпортоване збереження'),
  session: text('Session entry', 'Вхід у гру'),
  other: text('Other commands', 'Інші команди'),
  unknown: text('Unnamed write path', 'Шлях запису без джерела'),
  unattributed: text('No known source', 'Без відомого джерела'),
};

/** The command ops by income category; an op not named here is 'other' (it may change no balance upwards). */
const OP_CATEGORY: Readonly<Record<string, IncomeCategory>> = {
  // Starting over makes a new profile (createNewParams 'null': 800 gold, 0 diamonds): its gold is a starting capital,
  // more than the player had when the old balance was below it.
  'progress.startOver': 'start',
  'battle.finish': 'battle',
  'grave.search': 'battle',
  'achiev.claim': 'achievement',
  'task.claim': 'task',
  'present.claim': 'present',
  'tribute.claim': 'tribute',
  'store.redeem': 'purchase',
  'bank.exchange': 'exchange',
  'arsenal.sell': 'sale',
  'arsenal.sellWeapon': 'sale',
  'shop.buy': 'shop',
  'papa.buy': 'shop',
  'arsenal.upgrade': 'shop',
  'craft.assemble': 'shop',
  'craft.disassemble': 'shop',
  'skills.commit': 'shop',
  'perks.reset': 'shop',
  'spec.choose': 'shop',
  'roulette.bet': 'shop',
  'steal.attempt': 'shop',
  'steal.apology': 'shop',
  'auction.buy': 'auction',
};

/** The ops whose category is 'other' on purpose (they change no balance): the coverage test lists every client op. */
export const NEUTRAL_OPS: readonly string[] = [
  'time.refresh',
  'tutorial.close',
  'settings.set',
  'names.set',
  'papa.enter',
  'papa.ask',
  'arsenal.stimToFight',
  'arsenal.equip',
  'arsenal.readBook',
  'arsenal.clearQuickSlot',
  'battle.start',
  'battle.forfeit',
];

export const CATEGORIZED_OPS: readonly string[] = Object.keys(OP_CATEGORY);

export function incomeCategory(s: EconomySource): IncomeCategory {
  switch (s.kind) {
    case 'command':
      return OP_CATEGORY[s.op ?? ''] ?? 'other';
    case 'session':
      return 'session';
    case 'start':
      return 'start';
    case 'purchase':
      return 'purchase';
    case 'refund':
      return 'refund';
    case 'ad':
      return 'ad';
    case 'admin':
      return 'admin';
    case 'client_save':
      return 'client_save';
    case 'import':
      return 'import';
    default:
      return 'unknown';
  }
}

/** The starting capital of profile.create (src/game/rules/ops/progress-profile.ts create_new_params; dev: the developer profile). */
export const START_CAPITAL: Readonly<CurrencyPair> = { gold: 800, diam: 0 };
export const DEV_CAPITAL: Readonly<CurrencyPair> = { gold: 5_000_000, diam: 5000 };
/** The largest daily present (PRESENT_GOLD; the 9th day gives one diamond). */
export const PRESENT_MAX: Readonly<CurrencyPair> = { gold: Math.max(...Object.values(PRESENT_GOLD)), diam: 1 };

/**
 * Categories whose income counts towards the income rate of the game's own sources (rateIncome): the recurring income
 * of play. The achievements and tasks are not in it: they are one-off milestone prizes of the game's tables that the
 * server's rules compute from the canonical counters, and a long-time player's single claim legitimately exceeds an
 * hour's income (the "days in the game" achievement alone gives up to 1000 diamonds a level, a kill or assault
 * achievement up to 4 / 8 million gold).
 */
export const GAME_INCOME: ReadonlySet<IncomeCategory> = new Set<IncomeCategory>(['battle', 'present', 'tribute', 'sale', 'client_save']);
/** Categories whose spending counts towards a spree (spending of unexplained resources). */
export const SPENDING: ReadonlySet<IncomeCategory> = new Set<IncomeCategory>(['shop', 'auction', 'exchange']);

/** What may grow, per category (true = any amount; the capped ones are checked in outsideEnvelope). */
const MAY_GAIN: Readonly<Record<IncomeCategory, { gold: boolean; diam: boolean }>> = {
  start: { gold: true, diam: true },
  battle: { gold: true, diam: true },
  achievement: { gold: true, diam: true },
  task: { gold: true, diam: true },
  present: { gold: true, diam: true },
  tribute: { gold: true, diam: false },
  ad: { gold: true, diam: true },
  purchase: { gold: true, diam: true },
  refund: { gold: false, diam: false },
  exchange: { gold: true, diam: false },
  sale: { gold: true, diam: false },
  shop: { gold: false, diam: false },
  auction: { gold: false, diam: false },
  admin: { gold: true, diam: true },
  client_save: { gold: true, diam: true },
  import: { gold: true, diam: true },
  session: { gold: false, diam: false },
  other: { gold: false, diam: false },
  unknown: { gold: false, diam: false },
};

/** The part of an increase its source cannot give, and why (null: within the envelope). */
export interface EnvelopeExcess {
  gold: number;
  diam: number;
  why: Text;
}

const pos = (n: number) => (n > 0 ? n : 0);

/**
 * The part of a change that its source cannot explain. `delta` = after - before; `source` the change's source; only
 * increases are ever outside an envelope (spending is never suspicious by itself).
 */
export function outsideEnvelope(category: IncomeCategory, source: EconomySource, delta: CurrencyPair): EnvelopeExcess | null {
  let gold = 0;
  let diam = 0;
  let why: Text | null = null;
  const may = MAY_GAIN[category];
  if (!may.gold) gold = pos(delta.gold);
  if (!may.diam) diam = pos(delta.diam);
  if (gold > 0 || diam > 0) {
    why =
      category === 'unknown'
        ? text('the write named no source', 'запис не назвав джерела')
        : text(`${category}${source.op ? ` (${source.op})` : ''} never adds ${gold > 0 ? 'gold' : 'diamonds'}`, `${category}${source.op ? ` (${source.op})` : ''} ніколи не додає ${gold > 0 ? 'золота' : 'алмазів'}`);
  }
  if (category === 'exchange' && delta.gold > 0) {
    const allowed = pos(-delta.diam) * GOLD_PER_DIAMOND;
    if (delta.gold > allowed) {
      gold = delta.gold - allowed;
      why = text(`the exchange gives ${GOLD_PER_DIAMOND} gold per diamond: ${pos(-delta.diam)} diamonds give ${allowed}, not ${delta.gold}`, `обмін дає ${GOLD_PER_DIAMOND} золота за алмаз: ${pos(-delta.diam)} алмазів дають ${allowed}, а не ${delta.gold}`);
    }
  }
  if (category === 'present') {
    if (delta.gold > PRESENT_MAX.gold || delta.diam > PRESENT_MAX.diam) {
      gold = pos(delta.gold - PRESENT_MAX.gold);
      diam = pos(delta.diam - PRESENT_MAX.diam);
      why = text(`the daily present gives at most ${PRESENT_MAX.gold} gold or ${PRESENT_MAX.diam} diamond`, `щоденний подарунок дає щонайбільше ${PRESENT_MAX.gold} золота або ${PRESENT_MAX.diam} алмаз`);
    }
  }
  if (category === 'start') {
    const cap = source.dev ? DEV_CAPITAL : START_CAPITAL;
    if (delta.gold > cap.gold || delta.diam > cap.diam) {
      gold = pos(delta.gold - cap.gold);
      diam = pos(delta.diam - cap.diam);
      why = text(`a new profile starts with ${cap.gold} gold and ${cap.diam} diamonds`, `новий профіль починає з ${cap.gold} золота й ${cap.diam} алмазів`);
    }
  }
  return why && (gold > 0 || diam > 0) ? { gold, diam, why } : null;
}

/** The severity of an amount that appeared from no known source. */
export function unattributedSeverity(amount: CurrencyPair, high: CurrencyPair): MarkSeverity {
  return amount.gold >= high.gold || amount.diam >= high.diam ? 'high' : 'medium';
}

/** The severity of an income rate over the limit: 3x the limit is high. */
export function rateSeverity(income: CurrencyPair, limit: CurrencyPair): MarkSeverity | null {
  if (income.gold <= limit.gold && income.diam <= limit.diam) return null;
  return income.gold > 3 * limit.gold || income.diam > 3 * limit.diam ? 'high' : 'medium';
}

// ------------------------------------------------------------------------------------------------ battles

/** Battle ticks per second of real time: the game's fixed-step loop never runs faster (src/flash/player/FlashPlayer.ts). */
export const TICKS_PER_SECOND = 50;

/** A battle finished faster than its ticks can run: the severity (high below half of the allowed time), or null. */
export function fastFinish(ticks: number, elapsedMs: number, cfg: { fastRatio: number; fastMinTicks: number }): { severity: MarkSeverity; minimumMs: number } | null {
  if (!(ticks >= cfg.fastMinTicks) || !(elapsedMs >= 0)) return null;
  const minimumMs = (ticks / TICKS_PER_SECOND) * 1000;
  if (elapsedMs >= minimumMs * cfg.fastRatio) return null;
  return { severity: elapsedMs < minimumMs * cfg.fastRatio * 0.5 ? 'high' : 'medium', minimumMs };
}

/** Playable stages per mode (docs/economy.md §1.2): the first half of a mode is won more often. */
const MODE_STAGES: Readonly<Record<string, number>> = { career: 100, missions: 16, survival: 19, boss: 18 };

/**
 * The model probability of winning a new stage (docs/economy.md §1.6, tools/economy/model.mts: 65 % in the first half
 * of a mode, 35 % in the second half, 50 % against a boss), or null for battles the model does not rate (assault).
 */
export function newStageWinChance(mode: string, stage: number): number | null {
  if (mode === 'boss') return 0.5;
  const n = MODE_STAGES[mode];
  if (!n) return null;
  return stage <= n / 2 ? 0.65 : 0.35;
}

/** The score a won new stage adds to a streak: -log10 of its model probability. */
export function streakStep(mode: string, stage: number): number {
  const p = newStageWinChance(mode, stage);
  return p === null ? 0 : -Math.log10(p);
}

/** The violations of an 'invalid_record' message ("input the game could not have produced: tick 5: weapon: ..."). */
export function inputViolations(message: string | null): string[] {
  if (!message) return [];
  const i = message.indexOf(':');
  const rest = i >= 0 ? message.slice(i + 1) : message;
  return rest
    .split(';')
    .map((x) => x.trim())
    .filter((x) => x !== '')
    .slice(0, 5);
}

/** What kind of impossible input a violation is (weapon / click / other), for the reason text. */
export function violationKind(v: string): 'weapon' | 'click' | 'other' {
  if (/\bweapon\b/.test(v)) return 'weapon';
  if (/\bclick\b/.test(v)) return 'click';
  return 'other';
}
